import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Flex } from '@chakra-ui/react';

import { useQuery, useQueryClient } from 'react-query';
import { fetchHistory } from 'api/history';

import { useWashHubListener } from 'hooks';

import { Filters } from 'components/primitives/Filters';
import { StatCards } from 'components/primitives/StatCards';
import { DataTable } from 'components/modules/DataTable';

import { washHub } from 'store/slices';

import { useMutation } from 'react-query';
import { exportCsvItemsHistory } from 'api/items';

import { CSVLink } from 'react-csv';
import { History } from 'types/history';
import { useSites } from 'hooks/sites/useSites';
import { DateRangeInputExt } from 'components/modules/DateRangeInputExt';

import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

import { buildItemsHistoryColumns } from './itemsHistoryColumns';

export const ItemsHistory: FC<{ compType?: string }> = ({
  compType = 'item',
}) => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState<number>(1);
  const [sku, setSku] = useState<string[]>();
  const [venue, setVenue] = useState<string[]>();
  const [status, setStatus] = useState<number[]>();
  const [endDate, setEndDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const [type, setType] = useState<string>();
  const [urlRecord, setUrlRecord] = useState({});
  const queryUrl = useQueryParams();

  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';
  const washHubName = selectedWashHub?.name || '';
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const isUpdated = useUrlSharing(urlRecord, washHubId);

  const queryKey = [
    `fetch-history-${washHubId}`,
    page,
    status,
    sku,
    venue,
    endDate,
    startDate,
  ];

  const queryFn = () =>
    fetchHistory({
      sku,
      venue,
      page,
      status,
      limit: 10,
      washHub: washHubId,
      endDate,
      startDate,
    });

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
    refetchOnMount: 'always' as any,
  };

  const { data, isFetching, isLoading } = useQuery(queryKey, queryFn, options);

  useWashHubListener(() => {
    setPage(1);

    setSku(undefined);
    setVenue(undefined);
    setStatus(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
  });

  const history = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  useEffect(() => {
    const urlParams = {
      washhubId: String(washHubId),
      compType,
      ...(type ? { type } : {}),
      ...(venue && venue.length ? { venue: venue.join('-') } : {}),
      ...(status && status.length ? { status: status.join('-') } : {}),
      ...(sku && sku.length ? { sku: sku.join('-') } : {}),
      ...(startDate ? { startDate: `${startDate.valueOf()}` } : {}),
      ...(endDate ? { endDate: `${endDate.valueOf()}` } : {}),
    };
    setUrlRecord(urlParams);
  }, [washHubId, compType, status, venue, sku, type, endDate, startDate]);

  useEffect(() => {
    const { status, venue, sku, type, startDate, endDate } = queryUrl;
    status && setStatus(status.split('-').map((el) => +el));
    venue && setVenue(venue.split('-'));
    sku && setSku(sku.split('-'));
    type && setType(type);
    startDate && setStartDate(new Date(+startDate));
    endDate && setEndDate(new Date(+endDate));
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  useEffect(() => () => setUrlRecord({}), []);

  const onApplyFilters = (data: string[], type: string) => {
    setPage(1);
    setType(type);
    if (type === 'sku') setSku(data);
    if (type === 'site') setVenue(data);
  };

  const onClearFilters = () => {
    setPage(1);

    setSku(undefined);
    setVenue(undefined);
  };

  const onSearchPress = (startDate: Date | null, endDate: Date | null) => {
    setPage(1);

    if (startDate) {
      setStartDate(startDate);
    }

    if (endDate) {
      setEndDate(endDate);
    }
  };

  const onSearchClear = () => {
    setPage(1);
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const onUpdateClick = () => {
    onClearFilters();
    setStatus(undefined);

    queryClient.invalidateQueries(`fetch-history-${washHubId}`);
  };

  const csvRef = useRef<any>(null);

  const { mutate, data: csvData = '', isLoading: isExporting } = useMutation(
    exportCsvItemsHistory,
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.link?.click?.();
        }, 500);
      },
    },
  );
  const sites = useSites();
  const allSites = sites.data || [];

  const columns = useMemo(() => buildItemsHistoryColumns(allSites), [allSites]);

  const renderExpBtn = () => {
    return (
      <>
        <Button
          isLoading={isExporting}
          minW="250px"
          onClick={() => {
            mutate({
              sku,
              venue,
              status,
              washHub: washHubId,
              startDate,
              endDate,
            });
          }}>
          Export Items history to CSV
        </Button>
        {!isExporting && Boolean(csvData) && (
          <CSVLink
            data={csvData}
            ref={csvRef}
            filename={`washhub-${washHubName}-items-history.csv`}
            target="_blank"
          />
        )}
      </>
    );
  };

  const renderRefreshButton = () => {
    return (
      <Button isLoading={isExporting} ml={2} onClick={onUpdateClick}>
        Update Table
      </Button>
    );
  };

  if (!isUrlMapped) return null;

  return (
    <>
      <DateRangeInputExt
        startDate={startDate}
        endDate={endDate}
        onSearchClear={onSearchClear}
        onSearchPress={onSearchPress}
      />
      <Flex mb={5}>
        {renderExpBtn()}
        {renderRefreshButton()}
      </Flex>
      <StatCards
        hideValue
        type="items"
        selected={status}
        onSelectStatus={(_selected) => {
          if (Array.isArray(_selected)) {
            setPage(1);
            setStatus(_selected);
          }
        }}
      />
      <Filters
        venue={type ? (type == 'sku' ? sku : venue) : []}
        isFullButton
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
      />
      <DataTable<History>
        minW="1700px"
        data={history}
        columns={columns}
        isLoading={isLoading || isFetching}
        pagination={pagination}
      />
    </>
  );
};
